import { Injectable } from '@angular/core';
import { CookieService, CookieOptions } from '@app/core/cookies';
import { Store } from '@ngrx/store';
import * as UserActions from '@app/core/user/store/actions';
import { IAppState } from '@app/store';
import { IUserPrivacySettings } from '@app/core/user/store/reducers/user.reducers';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { environment } from '@env/environment';

@Injectable()
export class PrivacyPolicyService {
  private cookieName = 'privacy_policy_1.1';

  private cookieOptions: CookieOptions = {
    domain: environment.cookieOptions.domain,
    expires: new Date('2035-01-24T08:17:00+00:00'),
    path: '/'
  };

  private actualSettings = {};

  // BE CAREFUL SETTINGS ARE USED BOTH IN LEGACY AND NGX WITH COOKIES. THEY HAVE TO REMAIN IDENTICAL.
  private initialSettings = {
    showBanner: true,
    acceptCookies: 'default_false',
    acceptRetargeting: 'default_false',
    acceptAnalytics: 'default_false'
  };

  private defaultAcceptanceSettings = {
    showBanner: false,
    acceptCookies: 'default_true',
    acceptRetargeting: 'default_true',
    acceptAnalytics: 'default_true'
  };

  constructor(
    private plateformService: PlatformService,
    private cookieService: CookieService,
    private store: Store<IAppState>
  ) {}

  initSettings() {
    this.actualSettings = this.getFromCookie();
    if (!this.actualSettings) {
      // use initial privacy settings on first load
      this.setInitialSettings();
    } else if (this.hasDefaultSettings()) {
      // use default acceptance privacy settings on second load
      this.setDefaultAcceptance();
    } else {
      // update store with existing privacy settings on other cases
      this.store.dispatch({
        type: UserActions.INIT_PRIVACY_SETTINGS,
        payload: this.actualSettings
      });
    }
  }

  checkDefaultAcceptance() {
    if (this.hasDefaultSettings()) {
      this.setDefaultAcceptance();
    }
  }

  hasDefaultSettings(): boolean {
    if (this.plateformService.isBrowser) {
      const currentSettings = this.getFromCookie();
      return JSON.stringify(currentSettings) === JSON.stringify(this.initialSettings);
    } else {
      return false;
    }
  }

  getFromCookie(): object {
    return this.cookieService.getObject(this.cookieName);
  }

  setInitialSettings(): void {
    this.cookieService.setObject(this.cookieName, this.initialSettings, this.cookieOptions);
    this.store.dispatch({
      type: UserActions.INIT_PRIVACY_SETTINGS,
      payload: this.initialSettings
    });
  }

  setDefaultAcceptance(): void {
    this.cookieService.setObject(this.cookieName, this.defaultAcceptanceSettings, this.cookieOptions);
    this.store.dispatch({
      type: UserActions.UPDATE_PRIVACY_SETTINGS,
      payload: this.defaultAcceptanceSettings
    });
  }

  updateSettings(config: IUserPrivacySettings): void {
    this.cookieService.setObject(this.cookieName, config, this.cookieOptions);
    this.store.dispatch({
      type: UserActions.UPDATE_PRIVACY_SETTINGS,
      payload: config
    });
  }
}
